import {
  notifyForbidden,
  notifyUnauthorized,
} from "../auth/authSessionHandler";
import { notifyConsentRequired } from "../auth/authConsentHandler";
import { API_BASE_URL } from "../config/apiBaseUrl.js";

export { API_BASE_URL };

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let csrfToken = null;
let csrfFetchPromise = null;

export async function ensureCsrfToken() {
  if (csrfToken) {
    return csrfToken;
  }

  if (!csrfFetchPromise) {
    csrfFetchPromise = (async () => {
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/api/auth/csrf`, {
          credentials: "include",
        });
      } catch {
        const hint = API_BASE_URL
          ? `Unable to reach the API at ${API_BASE_URL}.`
          : "Unable to reach the API. Start the server on port 5001 and use npm start in client/.";
        throw new Error(hint);
      }

      const body = await response.json().catch(() => null);
      if (!response.ok || !body?.csrfToken) {
        throw new Error(body?.message || "Unable to load CSRF token.");
      }
      csrfToken = body.csrfToken;
      return csrfToken;
    })().finally(() => {
      csrfFetchPromise = null;
    });
  }

  return csrfFetchPromise;
}

export function resetCsrfToken() {
  csrfToken = null;
}

function isCsrfErrorMessage(message) {
  return typeof message === "string" && message.toLowerCase().includes("csrf");
}

export async function requestJson(path, options = {}) {
  const { skipAuthRedirect, signal, _csrfRetried, ...fetchOptions } = options;
  const method = (fetchOptions.method || "GET").toUpperCase();
  const headers = {
    ...(fetchOptions.headers || {}),
  };

  if (MUTATING_METHODS.has(method)) {
    if (!headers["Content-Type"] && !headers["content-type"]) {
      headers["Content-Type"] = "application/json";
    }

    const token = await ensureCsrfToken();
    headers["X-CSRF-Token"] = token;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    method,
    headers,
    credentials: "include",
    signal,
  });

  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    let message =
      body?.message || body?.error || `Request failed (${response.status})`;
    if (response.status === 404) {
      message = `${message} (${path})`;
    }

    if (
      response.status === 403 &&
      isCsrfErrorMessage(message) &&
      !_csrfRetried
    ) {
      resetCsrfToken();
      return requestJson(path, { ...options, _csrfRetried: true });
    }

    if (!skipAuthRedirect) {
      if (response.status === 401) {
        notifyUnauthorized();
      } else if (response.status === 403 && !isCsrfErrorMessage(message)) {
        notifyForbidden();
      } else if (response.status === 428) {
        notifyConsentRequired(body);
      }
    }

    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
}

export function buildQuery(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}
